<template>
  <div>
    <ValidationObserver
      ref="observer"
      v-slot="{ valid, dirty }"
    >
      <v-col class="subtitle">
        {{ $t('forms.labels.for') }}
      </v-col>
      <v-col>
        <VAutocomplete
          v-model="data.analysisTypes"
          :label="$t('forms.labels.analyses')"
          itemValue="id"
          itemText="name"
          :multiple="true"
          outlined
          :items="displayedAnalysisTypes"
          data-cy="AnalysisAutomaticSentence"
          @input="updateData"
        />
      </v-col>
      <v-col>
        <VAutocomplete
          ref="autocompleteRef"
          v-model="data.componentTypes"
          :label="$t('forms.labels.components')"
          :multiple="true"
          itemValue="id"
          itemText="name"
          :items="displayedComponentTypes"
          outlined
          data-cy="ComponentsAutomaticSentence"
          @change="updateData"
        >
          <template #withInfiniteScroll>
            <div v-intersect="endIntersect" />
          </template>
        </VAutocomplete>
      </v-col>
      <v-col class="subtitle">
        {{ $t('forms.labels.category') }}
      </v-col>
      <v-col>
        <VSelect
          v-model="data.category"
          itemValue="id"
          :rules="{ required: true}"
          :loading="loadingCategories"
          :label="$t('forms.labels.category')"
          outlined
          :items="displayedCategories"
          data-cy="CategoryAutomaticSentence"
          @input="updateData"
        />
      </v-col>
      <v-col class="subtitle">
        {{ $t('comments.label') }}
      </v-col>
      <v-col>
        <VTextField
          v-model="data.title[selectedLang]"
          :label="$t(`forms.labels.title_${selectedLang}`)"
          outlined
          :rules="{ required: selectedLang === teamMainLang, min: 1, max: 200 }"
          @input="updateData"
        />
      </v-col>
      <v-col>
        <VTextarea
          v-model="data.sentence[selectedLang]"
          :label="$t(`forms.labels.sentence_${selectedLang}`)"
          :rules="{ required: selectedLang === teamMainLang, min: 1, max: 2000 }"
          @input="updateData"
        />
      </v-col>
      <v-col>
        <VCheckbox
          v-model="data.showInComments"
          :label="$t('forms.labels.showInComments')"
          :rules="[{ required: true }]"
          :showCustomLabel="true"
          :color="'indigo darken-3'"
          @input="updateData"
        />
      </v-col>
      <SlotScopeWatcher
        :value="dirty"
        @change="$emit('dirtyChanged', dirty)"
      />
      <SlotScopeWatcher
        :value="valid"
        @change="$emit('validChanged', valid)"
      />
    </ValidationObserver>
  </div>
</template>

<script>
import { isPlainObject } from 'lodash';
import { mapGetters } from 'vuex';
import { ValidationObserver } from 'vee-validate';
import SlotScopeWatcher from '@/components/shared/widgets/forms/validationHelper/SlotScopeWatcher';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';
import VTextarea from '@/components/shared/widgets/forms/VuetifyInputs/VTextarea.vue';
import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete.vue';
import VCheckbox from '@/components/shared/widgets/forms/VuetifyInputs/VCheckbox.vue';

export default {
    components: {
        ValidationObserver,
        SlotScopeWatcher,
        VTextField,
        VSelect,
        VTextarea,
        VAutocomplete,
        VCheckbox,
    },
    props: {
        sentence: {
            type: Object,
            default: () => ({}),
        },
        isEdition: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            data: {
                title: { },
                sentence: { },
                showInComments: false,
            },
            categories: [],
            loadingCategories: true,
            analysisTypes: [],
            componentTypes: [],
            pagination: {},
            queryParams: { page: 1, perpage: 25, search: '' },
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'selectedLang',
            'teamMainLang',
            'lang',
        ]),
        displayedAnalysisTypes() {
            return this.analysisTypes.map(({ name, id }) => ({
                name: name[this.userLang],
                id,
            }));
        },
        displayedComponentTypes() {
            return this.componentTypes.map(({ name, id }) => ({
                name: name[this.userLang],
                id,
            }));
        },
        displayedCategories() {
            return this.categories?.map(category => ({
                ...category,
                name: category.name[this.userLang] || category.name[this.teamMainLang],
            }));
        },
    },
    async created() {
        if (this.isEdition) {
            this.data = this.prepareData(this.sentence);
        }

        await this.getCategories();
        await this.getAnalysisTypes();
        await this.getComponentTypes();
    },
    mounted() {
        // Validate at start
        if (this.isEdition) {
            this.$refs.observer.validate();
        }
    },
    methods: {
        prepareData(sentence) {
            const { category, analysisTypes, componentTypes, ...data } = sentence;

            if (isPlainObject(category)) {
                data.category = category.id;
                data.analysisTypes = analysisTypes.map(({ id }) => id);
                data.componentTypes = componentTypes.map(({ id }) => id);
            }

            return data;
        },
        async getCategories() {
            const { data } = await this.$rq.getSentenceCategories();
            this.categories = data.categories;
            this.loadingCategories = false;
        },
        async getAnalysisTypes() {
            const { data } = await this.$rq.getAnalysisTypes({ pagination: false });
            this.analysisTypes = data.types;
        },
        async getComponentTypes() {
            if (this.isEdition) {
                const { data } = await this.$rq.getComponentTypes({ pagination: false });
                this.componentTypes = data.componentTypes;
            } else if (this.pagination?.currentPage !== this.queryParams?.page
            && this.pagination?.totalPageCount ? this.pagination?.totalPageCount >= this.queryParams?.page : true) {
                const { data } = await this.$rq.getComponentTypes(this.queryParams);
                this.componentTypes = [...data.componentTypes, ...this.componentTypes];
                this.pagination = data.pagination;
            }
        },
        updateData() {
            this.$emit('sendData', this.data);
        },

        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination?.currentPage < this.pagination?.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getComponentTypes();
                // access "v-autocomplete" ref
                const ref = this.$refs?.autocompleteRef?.$refs?.autocomplete;
                if (ref?.onScroll) {
                    // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
::v-deep .subtitle {
  color: black;
  font-size: 16px;
  font-weight: bold;
}

</style>
